import Phaser from 'phaser';
import { type PersonalityId } from '../../game/ai/personality.js';
import {
  type GameSetup,
  type SeatConfig,
  defaultSeats,
  setupIsWinnable,
} from '../../game/setup.js';
import { DEFAULT_MAP, MAPS } from '../../maps/index.js';
import { attachBottomNav, preloadNavAssets } from '../view/AppNav.js';
import { Button } from '../view/Button.js';
import { navigateBack } from '../sceneNav.js';
import { COLORS, FONT_HEADER, FONT_MONO } from '../style.js';
import { DESIGN_HEIGHT, DESIGN_WIDTH } from '../PhaserGame.js';

// --- Layout constants ---
//
// IMPORTANT: do NOT compute module-level constants from
// DESIGN_WIDTH/DESIGN_HEIGHT — Phaser's scene-registration order
// imports this file before PhaserGame finishes initializing those
// exports, which throws a TDZ error at load. Constants that need
// the design dimensions are computed inside method bodies instead.

const HEADER_BAND_HEIGHT = 96;
const HEADER_LABEL_Y = 60;

const PILL_HEIGHT = 64;
const MAP_PILL_WIDTH = 168;
const MAP_PILL_GAP = 12;

const MAP_LABEL_Y = HEADER_BAND_HEIGHT + 48;
const MAP_PILLS_Y = MAP_LABEL_Y + 56;

const SEATS_LABEL_Y = MAP_PILLS_Y + 90;
const FIRST_SEAT_ROW_Y = SEATS_LABEL_Y + 50;
const SEAT_ROW_HEIGHT = 80;
const SEAT_ROW_SPACING = 8;
const SEAT_ROW_PITCH = SEAT_ROW_HEIGHT + SEAT_ROW_SPACING;

const SEAT_COUNT_LABEL_Y = FIRST_SEAT_ROW_Y + 4 * SEAT_ROW_PITCH + 24;
const SEAT_COUNT_PILLS_Y = SEAT_COUNT_LABEL_Y + 56;

const WARNING_Y = SEAT_COUNT_PILLS_Y + 56;
const START_BUTTON_Y = WARNING_Y + 60;

// Seat row layout (within the centered card). Horizontal positions
// derived from DESIGN_WIDTH live in method-local helpers below.
const ROW_LEFT = 36;
const ROW_RIGHT_INSET = 36;
const SWATCH_SIZE = 44;
const SWATCH_X = ROW_LEFT + 32;
const NAME_X = SWATCH_X + 38;
const PERSONALITY_PILL_W = 108;
const TEAM_PILL_W = 56;
const TEAM_PILL_RIGHT_INSET = 16;
const PILL_INNER_HEIGHT = 36;

function rowRight(): number {
  return DESIGN_WIDTH - ROW_RIGHT_INSET;
}
function rowWidth(): number {
  return rowRight() - ROW_LEFT;
}
function personalityPillX(): number {
  return rowRight() - 80 - PERSONALITY_PILL_W / 2;
}
function teamPillX(): number {
  return rowRight() - TEAM_PILL_RIGHT_INSET - TEAM_PILL_W / 2;
}

const PERSONALITY_CYCLE: readonly PersonalityId[] = [
  'aggressive',
  'cautious',
  'wildcard',
];

/** Compact 3-4 char labels — full names overflow the row at 80px tall. */
const PERSONALITY_SHORT: Readonly<Record<PersonalityId, string>> = {
  aggressive: 'AGG',
  cautious: 'CAU',
  wildcard: 'WILD',
};

const TEAM_CYCLE: readonly number[] = [1, 2, 3, 4];

// --- Internal types ---

interface MapPickerEntry {
  readonly mapId: string;
  readonly bg: Phaser.GameObjects.Rectangle;
  readonly title: Phaser.GameObjects.Text;
  readonly meta: Phaser.GameObjects.Text;
}

interface SeatCountEntry {
  readonly count: 2 | 3 | 4;
  readonly bg: Phaser.GameObjects.Rectangle;
  readonly label: Phaser.GameObjects.Text;
}

interface SeatRow {
  readonly index: number;
  readonly bg: Phaser.GameObjects.Rectangle;
  readonly swatch: Phaser.GameObjects.Rectangle;
  readonly nameLabel: Phaser.GameObjects.Text;
  readonly humanTag: Phaser.GameObjects.Text | undefined;
  readonly personalityBg: Phaser.GameObjects.Rectangle | undefined;
  readonly personalityLabel: Phaser.GameObjects.Text | undefined;
  readonly teamBg: Phaser.GameObjects.Rectangle;
  readonly teamLabel: Phaser.GameObjects.Text;
}

/**
 * Pre-game configuration. Lets the user pick a map, seat count, AI
 * personalities, and team affiliations. Tapping START GAME passes the
 * assembled GameSetup to GameScene via scene data.
 *
 * Architectural note: kept as a separate scene so the multiplayer
 * flow (JoinScene, shipped in v0.8 Open Waters) could grow without
 * coupling to the single-player setup logic here.
 */
export class LobbyScene extends Phaser.Scene {
  // --- State ---
  // Always 4 seat configs in memory; visible count = `seatCount`.
  // Hidden seats preserve their personality / team across seat-count
  // changes (so cycling 4 → 3 → 4 brings the 4th seat back as you
  // configured it).
  private readonly seats: SeatConfig[] = [];
  private seatCount: 2 | 3 | 4 = 3;
  private selectedMapId = DEFAULT_MAP.id;

  // --- Render refs ---
  private mapPickerEntries: MapPickerEntry[] = [];
  private seatCountEntries: SeatCountEntry[] = [];
  private seatRows: SeatRow[] = [];
  private warningText!: Phaser.GameObjects.Text;
  private startButton!: Button;

  constructor() {
    super('Lobby');
  }

  preload(): void {
    preloadNavAssets(this);
  }

  create(): void {
    // scene.restart() reuses the JS instance; reset state on every entry.
    this.seats.length = 0;
    this.seats.push(...defaultSeats(4));
    this.seatCount = 3;
    this.selectedMapId = DEFAULT_MAP.id;
    this.mapPickerEntries = [];
    this.seatCountEntries = [];
    this.seatRows = [];

    this.drawBackground();
    this.drawHeader();
    this.drawMapPicker();
    this.drawSeatsLabel();
    this.drawSeatRows();
    this.drawSeatCountPicker();
    this.drawWarning();
    this.drawStartButton();
    // LobbyScene is a sub-screen of the games section (SP pre-game
    // setup), so re-tapping the highlighted games icon routes to
    // Inbox instead of being a no-op.
    attachBottomNav(this, 'games', { allowActiveTap: true });

    this.applyAllStyles();
  }

  // ------- Background & header -------

  private drawBackground(): void {
    const g = this.add.graphics();
    g.fillStyle(0x040713, 1);
    g.fillRect(0, 0, DESIGN_WIDTH, DESIGN_HEIGHT);
  }

  private drawHeader(): void {
    const band = this.add.graphics();
    band.fillStyle(0x040713, 1);
    band.fillRect(0, 0, DESIGN_WIDTH, HEADER_BAND_HEIGHT);
    band.lineStyle(1, COLORS.borderSubtleInt, 0.8);
    band.beginPath();
    band.moveTo(0, HEADER_BAND_HEIGHT);
    band.lineTo(DESIGN_WIDTH, HEADER_BAND_HEIGHT);
    band.strokePath();

    new Button(this, {
      x: 90,
      y: HEADER_LABEL_Y + 8,
      width: 120,
      height: 52,
      text: '< Back',
      // Pop the scene history (typically Home, but could be Inbox if
      // the user routed here via an AppNav tap on a future flow).
      onTap: () => navigateBack(this),
    });

    this.add
      .text(DESIGN_WIDTH / 2, HEADER_LABEL_Y + 8, 'WAR ROOM', {
        fontFamily: FONT_HEADER,
        fontSize: '24px',
        color: COLORS.accentCyanHex,
        fontStyle: '700',
      })
      .setLetterSpacing(4)
      .setOrigin(0.5);
  }

  // ------- Map picker -------

  private drawMapPicker(): void {
    this.add
      .text(DESIGN_WIDTH / 2, MAP_LABEL_Y, 'MAP', {
        fontFamily: FONT_HEADER,
        fontSize: '13px',
        color: COLORS.textMuted,
        fontStyle: '600',
      })
      .setLetterSpacing(4)
      .setOrigin(0.5);

    const totalWidth =
      MAPS.length * MAP_PILL_WIDTH + (MAPS.length - 1) * MAP_PILL_GAP;
    const startX = DESIGN_WIDTH / 2 - totalWidth / 2 + MAP_PILL_WIDTH / 2;

    MAPS.forEach((map, idx) => {
      const cx = startX + idx * (MAP_PILL_WIDTH + MAP_PILL_GAP);
      const bg = this.add.rectangle(
        cx,
        MAP_PILLS_Y,
        MAP_PILL_WIDTH,
        PILL_HEIGHT,
        COLORS.buttonBgInt
      );
      bg.setStrokeStyle(2, COLORS.buttonStrokeInt);
      bg.setInteractive({ cursor: 'pointer' });

      const title = this.add
        .text(cx, MAP_PILLS_Y - 10, map.name.toUpperCase(), {
          fontFamily: FONT_HEADER,
          fontSize: '18px',
          color: COLORS.textPrimary,
          fontStyle: '700',
        })
        .setLetterSpacing(2)
        .setOrigin(0.5);

      const meta = this.add
        .text(cx, MAP_PILLS_Y + 14, `${map.territories.length} TILES`, {
          fontFamily: FONT_MONO,
          fontSize: '11px',
          color: COLORS.textSecondary,
        })
        .setLetterSpacing(2)
        .setOrigin(0.5);

      this.mapPickerEntries.push({ mapId: map.id, bg, title, meta });
      bg.on('pointerdown', () => this.selectMap(map.id));
    });
  }

  private selectMap(id: string): void {
    if (this.selectedMapId === id) return;
    this.selectedMapId = id;
    this.applyMapPickerStyles();
  }

  private applyMapPickerStyles(): void {
    for (const entry of this.mapPickerEntries) {
      const selected = entry.mapId === this.selectedMapId;
      entry.bg.setFillStyle(
        selected ? COLORS.buttonBgPrimaryInt : COLORS.buttonBgInt
      );
      entry.bg.setStrokeStyle(
        selected ? 2 : 1,
        selected
          ? COLORS.buttonStrokePrimaryInt
          : COLORS.buttonStrokeInt
      );
      entry.title.setColor(
        selected ? COLORS.accentCyanHex : COLORS.textPrimary
      );
      entry.meta.setColor(
        selected ? COLORS.accentCyanHex : COLORS.textSecondary
      );
      entry.meta.setAlpha(selected ? 0.85 : 0.6);
    }
  }

  // ------- Seats label + rows -------

  private drawSeatsLabel(): void {
    this.add
      .text(DESIGN_WIDTH / 2, SEATS_LABEL_Y, 'SEATS', {
        fontFamily: FONT_HEADER,
        fontSize: '13px',
        color: COLORS.textMuted,
        fontStyle: '600',
      })
      .setLetterSpacing(4)
      .setOrigin(0.5);
  }

  private drawSeatRows(): void {
    for (let i = 0; i < 4; i++) {
      const cy = FIRST_SEAT_ROW_Y + i * SEAT_ROW_PITCH;
      this.seatRows.push(this.buildSeatRow(i, cy));
    }
  }

  /**
   * Build one seat row at vertical position `cy`. Seat 0 is the human
   * — locked, no personality picker, gets a "★ HUMAN" tag. Seats 1+
   * are AI with a tappable personality pill.
   */
  private buildSeatRow(index: number, cy: number): SeatRow {
    const isHuman = index === 0;

    // Card background (faint panel fill).
    const bg = this.add.rectangle(
      ROW_LEFT + rowWidth() / 2,
      cy,
      rowWidth(),
      SEAT_ROW_HEIGHT,
      COLORS.bgPanelInt,
      0.5
    );
    bg.setStrokeStyle(1, COLORS.borderSubtleInt, 0.7);

    // Color swatch on the left.
    const swatchColor = Phaser.Display.Color.HexStringToColor(
      this.seats[index]!.color
    ).color;
    const swatch = this.add.rectangle(
      SWATCH_X,
      cy,
      SWATCH_SIZE,
      SWATCH_SIZE,
      swatchColor
    );
    swatch.setStrokeStyle(2, COLORS.borderBrightInt, 0.8);

    // Name label (color name; human seat says "YOU").
    const displayName = isHuman ? 'YOU' : this.seats[index]!.name.toUpperCase();
    const nameLabel = this.add
      .text(NAME_X, cy, displayName, {
        fontFamily: FONT_HEADER,
        fontSize: '20px',
        color: COLORS.textPrimary,
        fontStyle: '700',
      })
      .setLetterSpacing(2)
      .setOrigin(0, 0.5);

    let humanTag: Phaser.GameObjects.Text | undefined;
    let personalityBg: Phaser.GameObjects.Rectangle | undefined;
    let personalityLabel: Phaser.GameObjects.Text | undefined;

    if (isHuman) {
      // Anchor the HUMAN tag where the personality pill would sit.
      humanTag = this.add
        .text(personalityPillX(), cy, '★ HUMAN', {
          fontFamily: FONT_MONO,
          fontSize: '14px',
          color: COLORS.textSecondary,
          fontStyle: '700',
        })
        .setLetterSpacing(2)
        .setOrigin(0.5);
    } else {
      personalityBg = this.add.rectangle(
        personalityPillX(),
        cy,
        PERSONALITY_PILL_W,
        PILL_INNER_HEIGHT,
        COLORS.buttonBgInt
      );
      personalityBg.setStrokeStyle(1, COLORS.buttonStrokeInt);
      personalityBg.setInteractive({ cursor: 'pointer' });
      personalityBg.on('pointerdown', () => this.cyclePersonality(index));

      personalityLabel = this.add
        .text(personalityPillX(), cy, '', {
          fontFamily: FONT_HEADER,
          fontSize: '14px',
          color: COLORS.accentCyanHex,
          fontStyle: '700',
        })
        .setLetterSpacing(2)
        .setOrigin(0.5);
    }

    // Team pill on the right (always present; tappable for all seats
    // — human players might want to ally with an AI).
    const teamBg = this.add.rectangle(
      teamPillX(),
      cy,
      TEAM_PILL_W,
      PILL_INNER_HEIGHT,
      COLORS.buttonBgInt
    );
    teamBg.setStrokeStyle(1, COLORS.buttonStrokeInt);
    teamBg.setInteractive({ cursor: 'pointer' });
    teamBg.on('pointerdown', () => this.cycleTeam(index));

    const teamLabel = this.add
      .text(teamPillX(), cy, '', {
        fontFamily: FONT_MONO,
        fontSize: '15px',
        color: COLORS.textPrimary,
        fontStyle: '700',
      })
      .setOrigin(0.5);

    return {
      index,
      bg,
      swatch,
      nameLabel,
      humanTag,
      personalityBg,
      personalityLabel,
      teamBg,
      teamLabel,
    };
  }

  private cyclePersonality(index: number): void {
    const current = this.seats[index]?.personality ?? 'aggressive';
    const cur = PERSONALITY_CYCLE.indexOf(current);
    const next =
      PERSONALITY_CYCLE[(cur + 1) % PERSONALITY_CYCLE.length]!;
    this.seats[index] = { ...this.seats[index]!, personality: next };
    this.applySeatRowStyles();
  }

  private cycleTeam(index: number): void {
    const current = this.seats[index]?.team ?? 1;
    const cur = TEAM_CYCLE.indexOf(current);
    const next = TEAM_CYCLE[(cur + 1) % TEAM_CYCLE.length]!;
    this.seats[index] = { ...this.seats[index]!, team: next };
    this.applySeatRowStyles();
    this.applyValidation();
  }

  private applySeatRowStyles(): void {
    for (const row of this.seatRows) {
      const seat = this.seats[row.index];
      const visible = row.index < this.seatCount;

      // Toggle visibility on every component this row owns.
      row.bg.setVisible(visible);
      row.swatch.setVisible(visible);
      row.nameLabel.setVisible(visible);
      row.humanTag?.setVisible(visible);
      row.personalityBg?.setVisible(visible);
      row.personalityLabel?.setVisible(visible);
      row.teamBg.setVisible(visible);
      row.teamLabel.setVisible(visible);

      // Disable hit areas when hidden so taps in the empty space don't
      // fire stale handlers.
      if (visible) {
        row.personalityBg?.setInteractive({ cursor: 'pointer' });
        row.teamBg.setInteractive({ cursor: 'pointer' });
      } else {
        row.personalityBg?.disableInteractive();
        row.teamBg.disableInteractive();
      }

      if (!visible || !seat) continue;

      if (row.personalityLabel && seat.personality) {
        row.personalityLabel.setText(PERSONALITY_SHORT[seat.personality]);
      }
      row.teamLabel.setText(`T${seat.team}`);
    }
  }

  // ------- Seat-count picker -------

  private drawSeatCountPicker(): void {
    this.add
      .text(DESIGN_WIDTH / 2, SEAT_COUNT_LABEL_Y, 'TOTAL SEATS', {
        fontFamily: FONT_HEADER,
        fontSize: '13px',
        color: COLORS.textMuted,
        fontStyle: '600',
      })
      .setLetterSpacing(4)
      .setOrigin(0.5);

    const counts: readonly (2 | 3 | 4)[] = [2, 3, 4];
    const PILL_W = 88;
    const PILL_GAP = 12;
    const totalWidth = counts.length * PILL_W + (counts.length - 1) * PILL_GAP;
    const startX = DESIGN_WIDTH / 2 - totalWidth / 2 + PILL_W / 2;

    counts.forEach((count, idx) => {
      const cx = startX + idx * (PILL_W + PILL_GAP);
      const bg = this.add.rectangle(
        cx,
        SEAT_COUNT_PILLS_Y,
        PILL_W,
        56,
        COLORS.buttonBgInt
      );
      bg.setStrokeStyle(2, COLORS.buttonStrokeInt);
      bg.setInteractive({ cursor: 'pointer' });

      const label = this.add
        .text(cx, SEAT_COUNT_PILLS_Y, String(count), {
          fontFamily: FONT_MONO,
          fontSize: '20px',
          color: COLORS.textPrimary,
          fontStyle: '700',
        })
        .setOrigin(0.5);

      this.seatCountEntries.push({ count, bg, label });
      bg.on('pointerdown', () => this.selectSeatCount(count));
    });
  }

  private selectSeatCount(c: 2 | 3 | 4): void {
    if (this.seatCount === c) return;
    this.seatCount = c;
    this.applySeatCountStyles();
    this.applySeatRowStyles();
    this.applyValidation();
  }

  private applySeatCountStyles(): void {
    for (const entry of this.seatCountEntries) {
      const selected = entry.count === this.seatCount;
      entry.bg.setFillStyle(
        selected ? COLORS.buttonBgPrimaryInt : COLORS.buttonBgInt
      );
      entry.bg.setStrokeStyle(
        selected ? 2 : 1,
        selected
          ? COLORS.buttonStrokePrimaryInt
          : COLORS.buttonStrokeInt
      );
      entry.label.setColor(
        selected ? COLORS.accentCyanHex : COLORS.textPrimary
      );
    }
  }

  // ------- Warning + Start -------

  private drawWarning(): void {
    this.warningText = this.add
      .text(
        DESIGN_WIDTH / 2,
        WARNING_Y,
        'ASSIGN AT LEAST 2 TEAMS TO START',
        {
          fontFamily: FONT_HEADER,
          fontSize: '13px',
          color: COLORS.accentAmberHex,
          fontStyle: '700',
        }
      )
      .setLetterSpacing(3)
      .setOrigin(0.5)
      .setVisible(false);
  }

  private drawStartButton(): void {
    this.startButton = new Button(this, {
      x: DESIGN_WIDTH / 2,
      y: START_BUTTON_Y,
      width: 320,
      height: 80,
      text: 'Start Game',
      style: 'primary',
      onTap: () => this.startGame(),
    });
  }

  private currentSetup(): GameSetup {
    return {
      mapId: this.selectedMapId,
      seats: this.seats.slice(0, this.seatCount),
    };
  }

  private applyValidation(): void {
    const winnable = setupIsWinnable(this.currentSetup());
    this.warningText.setVisible(!winnable);
    this.startButton.setEnabled(winnable);
  }

  private applyAllStyles(): void {
    this.applyMapPickerStyles();
    this.applySeatCountStyles();
    this.applySeatRowStyles();
    this.applyValidation();
  }

  private startGame(): void {
    if (!setupIsWinnable(this.currentSetup())) return;
    this.scene.start('Game', { setup: this.currentSetup() });
  }
}
